const STORAGE_KEY = 'vinyl_wish_list';
const CHANGE_EVENT = 'wishlist:change';

class WishListStore {
  static getHandles() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      const parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed.filter((h) => typeof h === 'string' && h.length > 0) : [];
    } catch {
      return [];
    }
  }

  static save(handles) {
    const unique = [...new Set(handles)];
    localStorage.setItem(STORAGE_KEY, JSON.stringify(unique));
    WishListCount.sync();
    document.dispatchEvent(
      new CustomEvent(CHANGE_EVENT, {
        detail: { handles: unique, count: unique.length },
      })
    );
  }

  static has(handle) {
    return WishListStore.getHandles().includes(handle);
  }

  static toggle(handle) {
    const handles = WishListStore.getHandles();
    const index = handles.indexOf(handle);
    if (index >= 0) {
      handles.splice(index, 1);
    } else {
      handles.unshift(handle);
    }
    WishListStore.save(handles);
    return index < 0;
  }

  static remove(handle) {
    WishListStore.save(WishListStore.getHandles().filter((h) => h !== handle));
  }
}

class WishListCount {
  static sync() {
    const count = WishListStore.getHandles().length;
    const label = count > 99 ? '99+' : count > 0 ? String(count) : '';

    document.querySelectorAll('[data-wish-list-count]').forEach((badge) => {
      badge.classList.toggle('is-visible', count > 0);
      badge.textContent = label;
    });

    document.querySelectorAll('[data-wish-list-anchor]').forEach((anchor) => {
      const label = count > 0 ? `Wish list, ${count} items` : 'Wish list';
      anchor.setAttribute('aria-label', label);
      anchor.setAttribute('title', label);
    });

    document.querySelectorAll('[data-wish-list-sr-only]').forEach((el) => {
      el.textContent = count > 0 ? `Wish list, ${count} items` : 'Wish list';
    });
  }
}

class WishListToggle extends HTMLElement {
  connectedCallback() {
    this.#button = this.querySelector('[data-wish-list-toggle]');
    this.#handle = this.dataset.productHandle;

    if (!this.#button || !this.#handle) return;

    this.#button.addEventListener('click', this.#onClick);
    document.addEventListener(CHANGE_EVENT, this.#sync);
    this.#sync();
  }

  disconnectedCallback() {
    this.#button?.removeEventListener('click', this.#onClick);
    document.removeEventListener(CHANGE_EVENT, this.#sync);
  }

  #button = null;
  #handle = '';

  #onClick = (event) => {
    event.preventDefault();
    WishListStore.toggle(this.#handle);
    this.#sync();
  };

  #sync = () => {
    const active = WishListStore.has(this.#handle);
    this.classList.toggle('is-active', active);
    this.#button?.setAttribute('aria-pressed', String(active));

    const label = this.#button?.querySelector('[data-wish-list-label]');
    if (label) {
      const strings = window.themeVariables?.strings?.wishList || {};
      label.textContent = active
        ? strings.remove || 'Remove from Wish list'
        : strings.add || 'Add to Wish list';
    }
  };
}

class WishListPage extends HTMLElement {
  connectedCallback() {
    this.#grid = this.querySelector('[data-wish-list-grid]');
    this.#empty = this.querySelector('[data-wish-list-empty]');
    document.addEventListener(CHANGE_EVENT, this.#render);
    this.#render();
  }

  disconnectedCallback() {
    document.removeEventListener(CHANGE_EVENT, this.#render);
  }

  #grid = null;
  #empty = null;

  #render = async () => {
    const handles = WishListStore.getHandles();

    if (!handles.length) {
      this.#grid?.replaceChildren();
      this.#empty?.removeAttribute('hidden');
      return;
    }

    this.#empty?.setAttribute('hidden', '');

    const products = (
      await Promise.all(
        handles.map(async (handle) => {
          try {
            const response = await fetch(`${Shopify.routes.root}products/${handle}.js`);
            if (!response.ok) return null;
            return response.json();
          } catch {
            return null;
          }
        })
      )
    ).filter(Boolean);

    if (!this.#grid) return;

    const strings = window.themeVariables?.strings?.wishList || {};
    const moneyFormat = window.themeVariables?.settings?.moneyFormat || '{{amount}}';

    this.#grid.innerHTML = products
      .map((product) => this.#itemHtml(product, strings, moneyFormat))
      .join('');

    this.#bindItemActions();
  };

  #bindItemActions() {
    this.#grid?.querySelectorAll('[data-wish-list-remove]').forEach((button) => {
      button.addEventListener('click', () => {
        WishListStore.remove(button.dataset.wishListRemove);
      });
    });
  }

  #formatMoney(cents, format) {
    const value = cents / 100;
    const amount = value.toFixed(2);
    const amountNoDecimals = Math.round(value).toString();
    const amountComma = amount.replace('.', ',');
    const amountNoDecimalsComma = Math.round(value).toString();

    return format.replace(
      /\{\{\s*([\w_]+)\s*\}\}/gi,
      (_, token) => {
        switch (token.toLowerCase()) {
          case 'amount':
            return amount;
          case 'amount_no_decimals':
            return amountNoDecimals;
          case 'amount_with_comma_separator':
            return amountComma;
          case 'amount_no_decimals_with_comma_separator':
            return amountNoDecimalsComma;
          case 'amount_with_apostrophe_separator':
            return amountComma.replace(/,/g, "'");
          default:
            return '';
        }
      }
    );
  }

  #priceLabel(product, moneyFormat) {
    const min = product.price_min ?? product.variants?.[0]?.price;
    const max = product.price_max ?? min;

    if (min == null) return '';

    if (max != null && max !== min) {
      return `${this.#formatMoney(min, moneyFormat)} – ${this.#formatMoney(max, moneyFormat)}`;
    }

    return this.#formatMoney(min, moneyFormat);
  }

  #itemHtml(product, strings, moneyFormat) {
    const variant =
      product.variants.find((v) => v.available) || product.variants[0];
    const image = product.featured_image || product.images?.[0];
    const imageSrc = typeof image === 'string' ? image : image?.src;
    const priceLabel = this.#priceLabel(product, moneyFormat);
    const hasMultipleVariants = product.variants.length > 1;
    const soldOut = !product.variants.some((v) => v.available);
    const modalId = `wish-list-qb-${product.id}`;

    const atcLabel = soldOut
      ? strings.soldOut || 'Sold out'
      : hasMultipleVariants
        ? strings.selectOptions || 'Select size'
        : strings.addToCart || 'Add to cart';

    const removeLabel = strings.removeItem || 'Remove';
    const removeIcon = `<svg aria-hidden="true" focusable="false" fill="none" width="16" viewBox="0 0 16 16"><path d="M2 4h12M5.333 4V2.667a1.333 1.333 0 0 1 1.334-1.334h2.666a1.333 1.333 0 0 1 1.334 1.334V4m2 0v9.333a1.333 1.333 0 0 1-1.334 1.334H4.667a1.333 1.333 0 0 1-1.334-1.334V4h9.334Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/><path d="M6.667 7.333v4M9.333 7.333v4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>`;

    const atcHtml = soldOut
      ? `<button type="button" class="wish-list-item__btn wish-list-item__btn--atc button button--sm button--outline" disabled>${atcLabel}</button>`
      : hasMultipleVariants
        ? `<quick-buy-modal id="${modalId}" handle="${product.handle}" class="quick-buy-modal modal modal--lg color-scheme color-scheme--dialog"></quick-buy-modal>
           <button type="button" class="wish-list-item__btn wish-list-item__btn--atc button button--sm button--outline" aria-controls="${modalId}" ${soldOut ? 'disabled' : ''}>${atcLabel}</button>`
        : `<form method="post" action="${Shopify.routes.root}cart/add" class="wish-list-item__atc-form">
             <input type="hidden" name="id" value="${variant.id}">
             <input type="hidden" name="quantity" value="1">
             <button type="submit" class="wish-list-item__btn wish-list-item__btn--atc button button--sm button--outline">${atcLabel}</button>
           </form>`;

    return `
      <article class="wish-list-item" data-handle="${product.handle}">
        <a href="${product.url}" class="wish-list-item__media">
          ${
            imageSrc
              ? `<img src="${imageSrc}" alt="${product.title.replace(/"/g, '&quot;')}" width="220" height="220" loading="lazy">`
              : ''
          }
        </a>
        <div class="wish-list-item__body">
          <a href="${product.url}" class="wish-list-item__title">${product.title}</a>
          ${priceLabel ? `<p class="wish-list-item__price text-subdued">${priceLabel}</p>` : ''}
        </div>
        <div class="wish-list-item__actions">
          <div class="wish-list-item__action-row">
            ${atcHtml}
            <button
              type="button"
              class="wish-list-item__remove-icon"
              data-wish-list-remove="${product.handle}"
              aria-label="${removeLabel}"
            >
              ${removeIcon}
            </button>
          </div>
        </div>
      </article>
    `;
  }
}

if (!customElements.get('wish-list-toggle')) {
  customElements.define('wish-list-toggle', WishListToggle);
}

if (!customElements.get('wish-list-page')) {
  customElements.define('wish-list-page', WishListPage);
}

const syncWishListCount = () => WishListCount.sync();

syncWishListCount();
document.addEventListener(CHANGE_EVENT, syncWishListCount);
window.addEventListener('pageshow', syncWishListCount);
